/* storybook-check-ignore */
import * as React from 'react';

import throttle from '@opendoor/bricks/helpers/throttle';

import NavigationBox from './NavigationBox';

export interface ILink {
  idToWatch: string;
  text: string;
}

export interface IScrollAwareNavigationBoxProps {
  algorithm?: 'elementMappedToScrollPosition' | 'highestAndBest';
  className?: string;
  links: Array<ILink>;
  render?: (
    props: IScrollAwareNavigationBoxProps,
    state: IScrollAwareNavigationBoxState,
    links: Array<{ active: boolean; href: string; text: string }>,
  ) => React.ReactNode;
  scrollContainer?: HTMLElement;
}

export interface IScrollAwareNavigationBoxState {
  activeId?: ILink['idToWatch'];
}

export default class ScrollAwareNavigationBox extends React.PureComponent<
  IScrollAwareNavigationBoxProps,
  IScrollAwareNavigationBoxState
> {
  public static defaultProps: Partial<IScrollAwareNavigationBoxProps> = {
    algorithm: 'highestAndBest',
  };

  public state: IScrollAwareNavigationBoxState = {};

  public handleChange = throttle(() => {
    this.checkElements();
  });

  public UNSAFE_componentWillReceiveProps(nextProps: IScrollAwareNavigationBoxProps) {
    if (this.props.scrollContainer !== nextProps.scrollContainer) {
      this.stopWatchingScrollContainer();
      this.watchScrollContainer(nextProps);
    }
    this.checkElements(nextProps);
  }

  public componentDidMount() {
    window.addEventListener('resize', this.handleChange, { passive: true });
    window.addEventListener('scroll', this.handleChange, { capture: true, passive: true });
    this.watchScrollContainer();
    this.handleChange();
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleChange);
    window.removeEventListener('scroll', this.handleChange);
    this.stopWatchingScrollContainer();
  }

  public watchScrollContainer = (props?: IScrollAwareNavigationBoxProps) => {
    const { scrollContainer } = props || this.props;
    if (!scrollContainer) {
      return;
    }

    scrollContainer.addEventListener('scroll', this.handleChange, { passive: true });
  };

  public stopWatchingScrollContainer = () => {
    const { scrollContainer } = this.props;
    if (!scrollContainer) {
      return;
    }

    scrollContainer.removeEventListener('scroll', this.handleChange);
  };

  // @ts-expect-error Previously existing error on legacy component
  public getElements = (links) => {
    return (
      links
        // @ts-expect-error Previously existing error on legacy component
        .map((link) => {
          return document.getElementById(link.idToWatch);
        })
        .filter((el: HTMLElement) => el && el.getBoundingClientRect)
    );
  };

  public checkElements = (otherProps?: IScrollAwareNavigationBoxProps) => {
    const { algorithm, links } = otherProps || this.props;
    const elements = this.getElements(links);
    if (elements.length === 0) {
      return;
    }

    const algo =
      algorithm === 'elementMappedToScrollPosition'
        ? this.determineElementMappedToScrollPosition
        : this.determineHighestAndBest;
    const activeElement = algo(elements);

    const activeId = activeElement && activeElement.id;
    this.setState({ activeId });
  };

  public determineHighestAndBest = (elements: Array<HTMLElement>): HTMLElement | undefined => {
    let highestVisibleElem: HTMLElement | undefined;
    let lowestHiddenElem: HTMLElement | undefined;
    let smallestTop = Infinity;
    let largestBottom = -Infinity;

    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();

      // bottom > 0 means the bottom of the element is not above the top of the
      // viewport. we're looking for the highest element where ^ is true
      if (rect.bottom > 0 && rect.top < smallestTop) {
        smallestTop = rect.top;
        highestVisibleElem = element;
        // if/when all elements are scrolled above top of the viewport, this
        // will be the fallback that selects the lowest element in the set of
        // tracked elements
      } else if (rect.bottom > largestBottom) {
        largestBottom = rect.bottom;
        lowestHiddenElem = element;
      }
    });

    return highestVisibleElem || lowestHiddenElem;
  };

  public determineElementMappedToScrollPosition = (
    elements: Array<HTMLElement>,
  ): HTMLElement | undefined => {
    const { scrollContainer } = this.props;
    const scrollContainerRect = scrollContainer
      ? scrollContainer.getBoundingClientRect()
      : { bottom: window.innerHeight, top: 0, height: window.innerHeight };
    const scrollPosition = scrollContainer ? scrollContainer.scrollTop : window.pageYOffset;
    const scrollableHeight = scrollContainer
      ? scrollContainer.scrollHeight
      : document.body.scrollHeight;
    const scrollContainerAbsoluteTop = scrollContainerRect.top - scrollPosition;
    const mappedPosition =
      (scrollPosition / (scrollableHeight - scrollContainerRect.height)) * scrollableHeight;

    let closestElement: HTMLElement | undefined;
    let smallestDistance = Infinity;
    for (const element of elements) {
      const rect = element.getBoundingClientRect();
      const relativeBottom = rect.bottom - scrollContainerAbsoluteTop;
      const relativeTop = rect.top - scrollContainerAbsoluteTop;
      if (mappedPosition >= relativeTop && mappedPosition <= relativeBottom) {
        return element;
      }

      const distance = Math.min(
        Math.abs(relativeBottom - mappedPosition),
        Math.abs(relativeTop - mappedPosition),
      );
      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestElement = element;
      }
    }

    return closestElement;
  };

  public render() {
    const { className, links, render } = this.props;
    const { activeId } = this.state;

    const formattedLinks = links.map((link) => ({
      active: activeId !== undefined && link.idToWatch === activeId,
      href: `#${link.idToWatch}`,
      text: link.text,
    }));

    if (render) {
      return render(this.props, this.state, formattedLinks);
    }

    return <NavigationBox className={className} links={formattedLinks} />;
  }
}
